import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BoltIcon from '@mui/icons-material/Bolt';
import ChatIcon from '@mui/icons-material/Chat';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LockIcon from '@mui/icons-material/Lock';
import ResponsiveModal from './common/ResponsiveModal';
import { useNotifications } from '../contexts/NotificationContext';

const BENEFITS: { icon: React.ReactNode; text: string }[] = [
  { icon: <BoltIcon fontSize="small" />, text: 'رویدادهای مهم سیستم را همان لحظه دریافت کنید' },
  { icon: <ChatIcon fontSize="small" />, text: 'تیکت‌ها و درخواست‌های جدید را فوری ببینید' },
  { icon: <StorefrontIcon fontSize="small" />, text: 'درخواست فروشندگان و تأییدها را از دست ندهید' },
];

/**
 * The single, app-level push opt-in dialog for the admin panel. Mounted once inside
 * NotificationProvider; open state + push controls come from NotificationContext.
 */
const EnablePushDialog: React.FC = () => {
  const theme = useTheme();
  const { pushDialogOpen, closePushDialog, enablePush, pushBusy, pushPermission } =
    useNotifications();

  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (pushDialogOpen) setDenied(pushPermission === 'denied');
  }, [pushDialogOpen, pushPermission]);

  const handleEnable = async () => {
    const result = await enablePush();
    if (result === 'granted') closePushDialog();
    else if (result === 'denied') setDenied(true);
  };

  const handleLater = () => closePushDialog();

  return (
    <ResponsiveModal
      open={pushDialogOpen}
      onClose={closePushDialog}
      disableClose={pushBusy}
      maxWidth="xs"
      title="اعلان‌ها را روشن کنید"
      subtitle="تا هیچ رویداد مهمی را از دست ندهید"
      icon={<NotificationsActiveIcon color="primary" />}
      actions={
        denied ? (
          <Button variant="contained" onClick={closePushDialog}>
            متوجه شدم
          </Button>
        ) : (
          <>
            <Button color="inherit" onClick={handleLater} disabled={pushBusy}>
              بعداً
            </Button>
            <Button
              variant="contained"
              onClick={handleEnable}
              disabled={pushBusy}
              startIcon={<NotificationsActiveIcon />}
              sx={{ fontWeight: 700 }}
            >
              {pushBusy ? 'در حال فعال‌سازی…' : 'فعال‌سازی اعلان‌ها'}
            </Button>
          </>
        )
      }
    >
      {denied ? (
        <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
          <LockIcon color="warning" sx={{ fontSize: 40 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            دسترسی اعلان‌ها مسدود شده است
          </Typography>
          <Typography variant="body2" color="text.secondary">
            مرورگر شما اجازهٔ نمایش اعلان‌ها را مسدود کرده است. برای فعال‌سازی، روی آیکن قفل (🔒)
            کنار نشانی سایت بزنید، بخش «اعلان‌ها / Notifications» را روی «اجازه دادن / Allow» بگذارید
            و صفحه را تازه‌سازی کنید.
          </Typography>
        </Stack>
      ) : (
        <>
          <Box
            sx={{
              borderRadius: 2,
              py: 2.5,
              mb: 2,
              textAlign: 'center',
              color: '#fff',
              overflow: 'hidden',
              position: 'relative',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.18)',
                '& .MuiSvgIcon-root': {
                  transformOrigin: 'top center',
                  animation: 'adminPushRing 2.8s ease-in-out infinite',
                },
                '@keyframes adminPushRing': {
                  '0%': { transform: 'rotate(0)' },
                  '5%': { transform: 'rotate(20deg)' },
                  '10%': { transform: 'rotate(-18deg)' },
                  '15%': { transform: 'rotate(14deg)' },
                  '20%': { transform: 'rotate(-10deg)' },
                  '25%': { transform: 'rotate(6deg)' },
                  '30%': { transform: 'rotate(0)' },
                  '100%': { transform: 'rotate(0)' },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  '& .MuiSvgIcon-root': { animation: 'none' },
                },
              }}
            >
              <NotificationsIcon sx={{ fontSize: 36 }} />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            با فعال‌سازی اعلان‌ها، رویدادهای مهم را همان لحظه روی مرورگرتان می‌بینید — حتی وقتی تب پنل
            بسته است.
          </Typography>

          <Stack spacing={1.5}>
            {BENEFITS.map((b, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    bgcolor: (t) =>
                      t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(178,83,115,0.08)',
                  }}
                >
                  {b.icon}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {b.text}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2.5, lineHeight: 1.8 }}
          >
            می‌توانید هر زمان از تنظیمات مرورگر این اجازه را لغو کنید.
          </Typography>
        </>
      )}
    </ResponsiveModal>
  );
};

export default EnablePushDialog;
